import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReCAPTCHA from 'react-google-recaptcha';

import { useAppDispatch, useRedirectAndScroll } from 'hooks';
import { checkEmailExist } from 'api/http-client/queries/accounts';
import { useAuth } from 'providers/auth-provider';
import { getLangFromLocalStorage, useTranslates } from 'providers/translates';
import { Loader } from 'components/Loader/Loader';
import { Helmet } from 'react-helmet-async';
import {
  colors,
  containOneDigitRegex,
  containOneSpecialCharRegex,
  containOneLowerCaseRegex,
  containOneUpperCaseRegex,
  emailRegex,
  passwordRegex,
  postalCodeRegex,
} from 'utilities';
import { DarkTooltip } from 'components/Tooltips/CustomTooltips';
import { setOpen } from 'store/reducers/notifySlice';

import styles from './signUp.module.scss';
import { ExistingUserPopup } from './components/ExistingUserPopup';
import { isVac } from 'utilities/is-vac';
import { vacSignUpLabel } from 'utilities/vac-copy';
import { Language } from 'providers/translates/enums';
import MarkdownRenderer from 'components/MarkdownRenderer';
import { interpolateMarkdown } from 'components/MarkdownRenderer/helpers';

interface SignUpForm {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  password: string;
  confirmPassword: string;
  postalCode: string;
  preferredCommunicationLanguage: string;
}

const translationsHelmet = {
  descriptionEN: "Sign up for a free account to start building a tribute quilt square to honour a Veteran's service.",
  descriptionFR: 'Créez un compte gratuit pour commencer à créer un carreau de courtepointe en hommage au service d’une vétérane ou d’un vétéran.',
  titleEN: 'Saluting Service | Sign up',
  titleFR: 'Saluons leur service | S’inscrire'
} as const;

export const LANGUAGES_EN = [
  {
    label: 'English',
    value: Language.ENGLISH
  },
  {
    label: 'French',
    value: Language.FRENCH
  }
];

export const LANGUAGES_FR = [
  {
    label: 'Anglais',
    value: Language.ENGLISH
  },
  {
    label: 'Français',
    value: Language.FRENCH
  }
];

const SignUp: React.FC = () => {
  const { register, isAuthorized, user } = useAuth();
  const { translates: t, selectedLang } = useTranslates();
  const emailErrorText = t?.signUp?.emailError;
  const passwordErrorText = t?.signUp?.passwordError;
  const isFrench = selectedLang === Language.FRENCH;
  const { onlyRedirect } = useRedirectAndScroll({});
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const dispatch = useAppDispatch();

  const [isRegistered, setRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAgreedPrivacy, setIsAgreed] = useState(false);
  const [isShowPrivacyModal, setIsShowPrivacy] = useState(false);
  const [showPass_1, setShowPass_1] = useState(false);
  const [showPass_2, setShowPass_2] = useState(false);
  const [termsIsRed, setTermsIsRed] = useState(false);
  const [commonError, setCommonError] = useState('');
  const [emailExistError, setEmailExistError] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const defaultLanguage = selectedLang ?? Language.ENGLISH;

  useEffect(() => {
    if (isAuthorized && !user?.isInvitedUser) {
      onlyRedirect('/profile', true);
    }
  }, [isAuthorized]);

  const { control, handleSubmit, watch } = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
      password: '',
      confirmPassword: '',
      postalCode: '',
      preferredCommunicationLanguage: isVac() ? defaultLanguage : Language.ENGLISH,
      captcha: false,
    },
  });

  const onSubmit = (data: SignUpForm) => {
    const { firstName, lastName, email, password, postalCode, preferredCommunicationLanguage } = data;
    setCommonError('');
    if (!isAgreedPrivacy) {
      setIsShowPrivacy(true);
      setTermsIsRed(true);
      return;
    }

    setIsLoading(true);
    register({
      email,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      password,
      postalCode,
      province: '',
      token: params.get('token') || '',
      preferredCommunicationLanguage
    })
      .then((resp) => {
        resp.returned.quiltId && onlyRedirect(`/update/${resp.returned.quiltId}`);
        setRegistered(true);
      })
      .catch((res) => {
        if (emailExistError) {
          setCommonError(emailErrorText?.accountAlreadyExists ? interpolateMarkdown(emailErrorText?.accountAlreadyExists, {email}) : `An account using the email '${email}' already exists.`);
          dispatch(setOpen(emailErrorText?.accountAlreadyExistsSignIn ? interpolateMarkdown(emailErrorText?.accountAlreadyExistsSignIn, {email}) : `An account using the email '${email}' already exists. Sign in.`));
        } else {
          const unableToRegister = emailErrorText?.unableToRegister ?? 'Unable to register, please try again later';
          setCommonError(res?.errors?.join(', ') || unableToRegister);
          dispatch(setOpen(res?.errors?.join(', ') || unableToRegister));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const checkEmail = (email: string) => {
    setEmailExistError(false);
    if (emailRegex.test(email)) {
      checkEmailExist(email)
        .then((res) => {
          if (!res.returned?.isFree) {
            setEmailExistError(true);
          }
        })
        .catch((err) => {
          dispatch(setOpen(emailErrorText?.checking ?? 'Error checking email'));
        });
    }
  };

  const checkPassword = (password: string) => {
    setPasswordError('');
    if (password.length === 0) {
      setPasswordError(passwordErrorText?.required ?? 'Password is required');
      return;
    }
    if (password.length < 6) {
      setPasswordError(passwordErrorText?.minLength ?? 'Password must be at least 6 characters');
      return;
    }
    if (!containOneDigitRegex.test(password)) {
      setPasswordError(passwordErrorText?.oneDigit ?? 'Password must contain at least one digit');
      return;
    }
    if (!containOneLowerCaseRegex.test(password)) {
      setPasswordError(passwordErrorText?.oneLowercaseLetter ?? 'Password must contain at least one lowercase letter');
      return;
    }
    if (!containOneUpperCaseRegex.test(password)) {
      setPasswordError(passwordErrorText?.oneUppercaseLetter ?? 'Password must contain at least one uppercase letter');
      return;
    }
    if (!containOneSpecialCharRegex.test(password)) {
      setPasswordError(passwordErrorText?.oneSpecialCharacter ?? 'Password must contain at least one special character');
      return;
    }
  };

  return (
    <>
    {isVac() && (
      <Helmet>
        <meta name="description" content={isFrench ? translationsHelmet?.descriptionFR : translationsHelmet?.descriptionEN} />
        <title>{isFrench ? translationsHelmet?.titleFR : translationsHelmet?.titleEN}</title>
      </Helmet>
    )}
    <section className={styles.signUp_section}>
      <div className={`center ${styles.create_quilt}`}>
        <h1 className="bigTitle">
          {t?.signUp?.createQuiltTitle ||
            'Create a Memorial Quilt Square in 4 Simple Steps'}
        </h1>
        <div className={styles.steps}>
          <div className={styles.step__item}>
            <div className={styles.step__num}>1</div>
            <div className={styles.step__info}>
              <h5 className="defaultTitle1">
                {t?.signUp?.stepTitle1 || 'Start a Quilt Square'}
              </h5>
              <p className="defaultText">
                {t?.signUp?.stepContent1 ||
                  'Set up an account and tell us who you are remembering.'}
              </p>
            </div>
          </div>
          <div className={styles.step__item}>
            <div className={styles.step__num}>2</div>
            <div className={styles.step__info}>
              <h5 className="defaultTitle1">{t?.signUp?.stepTitle2 || 'Invite Others to Create'}</h5>
              <p className="defaultText">
                {t?.signUp?.stepContent2 ||
                  'Invite people to contribute to the memorial quilt square.'}
              </p>
            </div>
          </div>
          <div className={styles.step__item}>
            <div className={styles.step__num}>3</div>
            <div className={styles.step__info}>
              <h5 className="defaultTitle1">
                {t?.signUp?.stepTitle3 || 'Add Stories and Photos'}
              </h5>
              <p className="defaultText">
                {t?.signUp?.stepContent3 ||
                  'Add photos, information, and memories about your person.'}
              </p>
            </div>
          </div>
          <div className={styles.step__item}>
            <div className={styles.step__num}>4</div>
            <div className={styles.step__info}>
              <h5 className="defaultTitle1">
                {t?.signUp?.stepTitle4 || 'Share Your Quilt Square'}
              </h5>
              <p className="defaultText">
                {t?.signUp?.stepContent4 ||
                  'Choose to share your quilt square publicly or with those you choose.'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className={`center ${styles.signUpBox}`}>
        {isRegistered && (
          <p style={{ textAlign: 'center', fontWeight: 500 }}>
            {t?.signUp?.verifyAccountMessage ||
              'Thanks for registering. Please check your email to verify your account.'}
          </p>
        )}
        {!isRegistered && (
          <>
            <h2 className="text-center defaultTitle">
              {t?.signUp?.readyTitle || 'Already have an account? Sign in'}
            </h2>
            <p className={styles.signUpBox__link}>
            {t?.signUp?.readyParagraph ?? 'Already have an account?'}{' '}<Link to={'/login'}>{t?.signUp?.readyLink ?? ' Sign In'}</Link>
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container rowSpacing={{ xs: 0, md: 1 }} columnSpacing={4}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <div className={`inputWithTitle ${styles.input_withTitle}`}>
                        <span>{t?.signUp?.firstName || 'First name'}</span>
                        <TextField
                          className="customInput"
                          error={!!error}
                          fullWidth
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          onBlur={onBlur}
                          helperText={
                            !!error
                              ? `${t?.signUp?.firstNameHelperTextError || 'First name is required'}`
                              : ''
                          }
                          FormHelperTextProps={{ style: { fontSize: 16 } }}
                        />
                      </div>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <div className={`inputWithTitle ${styles.input_withTitle}`}>
                        <span>{t?.signUp?.lastName || 'Last name'}</span>
                        <TextField
                          className="customInput"
                          error={!!error}
                          fullWidth
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          onBlur={onBlur}
                          helperText={
                            !!error
                              ? `${t?.signUp?.lastNameHelperTextError || 'Last name is required'}`
                              : ''
                          }
                          FormHelperTextProps={{ style: { fontSize: 16 } }}
                        />
                      </div>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: { value: true, message: 'Email is required' },
                      pattern: emailRegex,
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <div className={`inputWithTitle ${styles.input_withTitle}`}>
                        <span>{t?.signUp?.userEmail || 'Your email'}</span>
                        <TextField
                          className="customInput"
                          error={!!error || emailExistError}
                          fullWidth
                          value={value}
                          onChange={(e) => onChange(e.target.value.trim())}
                          onBlur={(e) => {
                            checkEmail(e.target.value.trim());
                            onBlur();
                          }}
                          helperText={
                            !!error
                              ? `${t?.login?.emailHelperTextError || error.message || 'Invalid email address'}`
                              : emailExistError
                                ? 'Email already exists'
                                : ''
                          }
                          FormHelperTextProps={{ style: { fontSize: 16 } }}
                        />
                      </div>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="confirmEmail"
                    control={control}
                    rules={{
                      required: { value: true, message: 'Email is required' },
                      validate: (val: string) => {
                        if (watch('email') !== val) {
                          return t?.signUp?.emailError?.notMatch ?? 'Email address does not match';
                        }
                      },
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <div className={`inputWithTitle ${styles.input_withTitle}`}>
                        <span>{t?.signUp?.confirmEmail || 'Confirm your email'}</span>
                        <TextField
                          className="customInput"
                          error={!!error}
                          fullWidth
                          value={value}
                          onChange={(e) => onChange(e.target.value.trim())}
                          onBlur={onBlur}
                          helperText={
                            !!error
                              ? `${t?.login?.emailHelperTextError || error.message || 'Invalid email address'}`
                              : ''
                          }
                          FormHelperTextProps={{ style: { fontSize: 16 } }}
                        />
                      </div>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{
                      required: { value: true, message: passwordErrorText?.required ?? 'Password is required' },
                      pattern: passwordRegex,
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <div className={`inputWithTitle ${styles.input_withTitle}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span>{t?.signUp?.userPassword || 'Password'}</span>
                        </div>
                        <TextField
                          className="customInput"
                          error={!!error || !!passwordError}
                          fullWidth
                          value={value}
                          onChange={(e) => onChange(e.target.value.trim())}
                          onBlur={(e) => checkPassword(e.target.value.trim())}
                          helperText={
                            !!passwordError
                              ? passwordError
                              : !!error
                                ? `${t?.login?.passwordHelperTextError || error.message || 'Incorrect password'}`
                                : ''
                          }
                          FormHelperTextProps={{ style: { fontSize: 16 } }}
                          type={showPass_1 ? 'text' : 'password'}
                          InputProps={{
                            endAdornment: (
                              <DarkTooltip
                                title={
                                  showPass_1
                                    ? `${t?.login?.passwordTooltipTitleHide || 'Hide password'}`
                                    : `${t?.login?.passwordTooltipTitleShow || 'Show password'}`
                                }
                              >
                                <IconButton onClick={() => setShowPass_1(!showPass_1)}>
                                  {showPass_1 ? (
                                    <VisibilityOffIcon />
                                  ) : (
                                    <VisibilityIcon color="inherit" />
                                  )}
                                </IconButton>
                              </DarkTooltip>
                            ),
                          }}
                        />
                      </div>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    rules={{
                      required: { value: true, message: passwordErrorText?.required ?? 'Password is required' },
                      validate: (val: string) => {
                        if (watch('password') !== val) {
                          return t?.signUp?.passwordError?.notMatch ?? 'Password does not match';
                        }
                      },
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <div className={`inputWithTitle ${styles.input_withTitle}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span>
                            {t?.signUp?.confirmPassword || 'Confirm your password'}
                          </span>
                        </div>
                        <TextField
                          className="customInput"
                          error={!!error}
                          fullWidth
                          value={value}
                          onChange={(e) => onChange(e.target.value.trim())}
                          onBlur={onBlur}
                          helperText={
                            !!error
                              ? `${t?.login?.passwordHelperTextError || error.message || 'Incorrect password'}`
                              : ''
                          }
                          FormHelperTextProps={{ style: { fontSize: 16 } }}
                          type={showPass_2 ? 'text' : 'password'}
                          InputProps={{
                            endAdornment: (
                              <DarkTooltip
                                title={
                                  showPass_2
                                    ? `${t?.login?.passwordTooltipTitleHide || 'Hide password'}`
                                    : `${t?.login?.passwordTooltipTitleShow || 'Show password'}`
                                }
                              >
                                <IconButton onClick={() => setShowPass_2(!showPass_2)}>
                                  {showPass_2 ? (
                                    <VisibilityOffIcon />
                                  ) : (
                                    <VisibilityIcon color="inherit" />
                                  )}
                                </IconButton>
                              </DarkTooltip>
                            ),
                          }}
                        />
                      </div>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <p className={`defaultText ${styles['grid-paragraph']}`}>
                    {t?.login?.passwordTooltipRequirements ||
                      'Password must be at least 6 characters and must include one lowercase letter, one uppercase letter, one number, and one special character (e.g., $, #, @).'}
                  </p>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="postalCode"
                    control={control}
                    rules={{
                      required: { value: true, message: 'Postal code is required' },
                      pattern: postalCodeRegex,
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <div className={`inputWithTitle ${styles.input_withTitle}`}>
                        <span>{t?.signUp?.postalCode || 'Postal code'}</span>
                        <TextField
                          className="customInput"
                          error={!!error}
                          fullWidth
                          value={value}
                          onChange={(e) => onChange(e.target.value.trim())}
                          onBlur={onBlur}
                          placeholder="K1A 0B1"
                          helperText={
                            !!error
                              ? `${t?.signUp?.postalCodeHelperTextError || error.message || 'For example: K1A 0B1'}`
                              : ''
                          }
                          FormHelperTextProps={{ style: { fontSize: 16 } }}
                        />
                      </div>
                    )}
                  />
                </Grid>
                {isVac() && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="preferredCommunicationLanguage"
                      control={control}
                      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                        <div className={`inputWithTitle ${styles.input_withTitle}`}>
                          <span>{t?.signUp?.preferredLanguageFieldName ||'Preferred Language'}</span>
                          <TextField
                            className={styles.customSelect}
                            select
                            error={!!error}
                            fullWidth
                            helperText={error ? error.message : ''}
                            FormHelperTextProps={{ style: { fontSize: 16 } }}
                            onChange={onChange} 
                            onBlur={onBlur}
                            value={value}
                          >
                            {isFrench ? (
                              LANGUAGES_FR.map((language) => (
                                <MenuItem key={language.value} value={language.value}>
                                  {language.label}
                                </MenuItem>
                              ))
                            ) : (
                              LANGUAGES_EN.map((language) => (
                                <MenuItem key={language.value} value={language.value}>
                                  {language.label}
                                </MenuItem>
                              ))
                            )}
                          </TextField>
                        </div>
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <p className={`defaultText ${styles['grid-paragraph']}`}>
                  {vacSignUpLabel(
                    selectedLang,
                    t?.signUp?.instructionsToConfirmEmail,
                    'instructionsToConfirmEmail',
                    'Once you click the “Create Account” button below you will receive an email from Canadian Healing Quilt confirming your account. Click the link in the email to complete account set up. If you don’t see an email from Canadian Healing Quilt in your inbox, check your Junk or Spam folders.'
                  )}
                  </p>
                </Grid>
              </Grid>
              {commonError && (
                <div className={styles.error_block}>
                  {commonError}{' '}
                  <Link className={styles.link} to={'/login'}>
                    {' '}
                     Sign in
                  </Link>
                </div>
              )}
              <Grid item xs={12} className={styles.reCaptcha}>
                <Controller
                  name="captcha"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: isVac()
                        ? (isFrench 
                            ? 'Vérifions que vous n’êtes pas un robot' 
                            : 'Please confirm that you are not a robot')
                        : (isFrench 
                            ? 'Vérifions que vous n’êtes pas un robot' 
                            : 'Please confirm that you are not a robot')
                    }
                  }}
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <>
                      {process.env.REACT_APP_RECAPTCHA_SECRET_KEY && (
                        <ReCAPTCHA
                          onChange={onChange}
                          sitekey={process.env.REACT_APP_RECAPTCHA_SECRET_KEY}
                          hl={isFrench ? 'fr' : 'en'}
                        />
                      )}
                      <p className={styles.errorMessage}>{error?.message}</p>
                    </>
                  )}
                />
              </Grid>
              <div className={styles.submit_block}>
                <div className={styles.signUpInput}>
                  <label className={styles.label}>
                    <input
                      type="checkbox"
                      name="memorial"
                      value="Private"
                      disabled={isLoading}
                      checked={isAgreedPrivacy}
                      onChange={() => {
                        if (isAgreedPrivacy) {
                          setIsAgreed(false);
                        } else {
                          setIsShowPrivacy(true);
                        }
                      }}
                    />
                    <span style={{ fontSize: '18px', color: termsIsRed ? colors.red : '#24476A' }}>
                      {t?.signUp?.signUpConsent ||
                        'I have reviewed the privacy terms and conditions'}
                    </span>
                  </label>
                </div>

                <button className="customButton" type="submit" disabled={isLoading}>
                  {t?.signUp?.signUp || 'CREATE ACCOUNT >'}
                </button>
              </div>
            </form>
          </>
        )}
        <Dialog
          fullWidth
          maxWidth="lg"
          open={isShowPrivacyModal}
          onClose={() => setIsShowPrivacy(false)}
        >
        {isVac() ? (
          <>
          <DialogTitle variant="h4" fontWeight={'bold'} className="defaultTitle1">
            {t?.signUp?.privacyTermsAndConditionsTitle ||
              'Saluting Service Privacy and Use Notice'}
          </DialogTitle>
          <DialogContent sx={{ maxHeight: '400px' }}>            
            <div className={`${styles.dialogItem} defaultText`}>
              <MarkdownRenderer 
                content={t?.signUp?.privacyTermsAndConditionsContent || ''} 
              />
            </div>
          </DialogContent>
          </>
         ) : (
          <>
            <DialogTitle variant="h4" fontWeight={'bold'} className="defaultTitle1">
            {t?.signUp?.privacyTermsAndConditionsTitle ||
              'Privacy and Use Notice'}
            </DialogTitle>
            <DialogContent sx={{ maxHeight: '400px' }}>
              <p className={`${styles.dialogItem} defaultText`}>
                The Canadian Healing Quilt is operated by the Canadian Virtual Hospice, a division of the International Centre for Dignity and Palliative Care, a registered charity.
              </p>
              <p className={`${styles.dialogItem} defaultText`}>
                By using the Canadian Healing Quilt you are agreeing to the{' '}
                <a
                  className={styles.customLink}
                  href="https://www.virtualhospice.ca/en_US/Main+Site+Navigation/Right+Footer+Navigation/Legal+Notices.aspx"
                  target="_blank"
                  rel="noreferrer"
                >
                  Legal Notices / Privacy Policy.
                </a>
              </p>
              <p className={`${styles.dialogItem} defaultText`}>
                By using the Canadian Healing Quilt, you also confirm the following:
              </p>
              <ul className={styles.privacyList}>
                <li className={`${styles.list_item} defaultText`}>
                  <span className={styles.list_dot}></span>
                  if you post to a Healing Quilt, that you have the right to post the photos and
                  contents you are posting to a Healing Quilt; and
                </li>
                <li className={`${styles.list_item} defaultText`}>
                  <span className={styles.list_dot}></span>
                  your use of the Canadian Healing Quilt will not be exploitive, obscene, harmful, threatening, 
                  abusive, harassing, hateful, defamatory, sexually explicit or pornographic, violent, inflammatory, 
                  or discriminatory based on race, sex, religion, nationality, disability, sexual orientation, or age 
                  or other such legally prohibited ground or be otherwise objectionable, such determination to be made 
                  in Canadian Virtual Hospice’s sole discretion.
                </li>
              </ul>
              <p className={`${styles.dialogItem} defaultText`}>
                For more information, please see the{' '}
                <a
                  className={styles.customLink}
                  href="https://www.virtualhospice.ca/en_US/Main+Site+Navigation/Right+Footer+Navigation/Legal+Notices.aspx"
                  target="_blank"
                  rel="noreferrer"
                >
                  Legal Notices / Privacy Policy
                </a>
                .
              </p>
              <p className={`${styles.dialogItem} defaultText`}>
                If you have any questions about the Canadian Healing Quilt or the privacy practices of
                Canadian Virtual Hospice, please contact{' '}
                <a 
                  className={styles.customLink}
                  href="mailto:info@virtualhospice.ca?subject=I have questions about the Canadian Healing Quilt"
                >
                  info@virtualhospice.ca
                </a>
                .
              </p>
            </DialogContent>
          </>
        )}
          <DialogActions sx={{ padding: '25px' }}>
            <button className="customButton" onClick={() => setIsShowPrivacy(false)}>
              {t?.buttonsForTheSite?.close || 'Close'}
            </button>
            <button
              className="customButton"
              onClick={() => {
                setIsAgreed(true);
                setTermsIsRed(false);
                setIsShowPrivacy(false);
              }}
            >
              {t?.buttonsForTheSite?.agree || 'Agree'}
            </button>
          </DialogActions>
        </Dialog>
        <ExistingUserPopup 
          isOpen={emailExistError}
          onClose={() => setEmailExistError(false)}
        />
        <Loader loading={isLoading} isWhite={false} />
      </div>
    </section>
  </>
  );
};

export default SignUp;
